import type { Database } from 'node-sqlite3-wasm';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { CliSqliteInterrupted, isCliSqliteLockError } from '../sqlite-errors.js';
import { logStaleSqliteLockIntervention } from '../sqlite-lock-path.js';
import {
  ASYNC_BASE_DELAY_MS,
  ASYNC_RETRY_MAX,
  asyncDelayMsInterruptible,
} from '../sqlite-lock-retry.js';
import type { SqliteDatabaseAccessOptions } from '../database-access-types.js';

export async function runSqliteWithAsync<OpenOptions, T>(
  config: SqliteDatabaseAccessOptions<OpenOptions>,
  openConnection: (options?: OpenOptions) => Database,
  safeClose: (db: Database | null | undefined) => void,
  fn: (db: Database) => T | Promise<T>,
  options?: OpenOptions,
  maxAttempts = ASYNC_RETRY_MAX,
): Promise<T> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (isCliImmediateShutdownRequested()) throw new CliSqliteInterrupted();
    let db: Database | undefined;
    try {
      db = openConnection(options);
      try {
        return await Promise.resolve(fn(db));
      } finally {
        safeClose(db);
      }
    } catch (e) {
      lastError = e;
      safeClose(db);
      if (e instanceof CliSqliteInterrupted) throw e;
      if (!isCliSqliteLockError(e)) throw e;
      config.onLockRetry?.({
        dbPath: config.getPath(),
        attempt,
        maxAttempts,
        final: attempt === maxAttempts,
      });
      if (attempt === maxAttempts) {
        logStaleSqliteLockIntervention(config.getPath());
        throw e;
      }
      const delayMs = Math.min(600, ASYNC_BASE_DELAY_MS * attempt);
      await asyncDelayMsInterruptible(delayMs);
      await yieldToEventLoop();
    }
  }
  if (lastError instanceof Error) throw lastError;
  throw new Error(String(lastError));
}
